import React, { useState, useEffect, useCallback, useRef } from 'react';
import LayersDetails from '@components/CustomFields/LayersDetails';
import CustomFieldsService from '@services/customFieldsService';
import CorgeeStorageHelper from '@components/Globals/CorgeeStorageHelper';
import HeaderDetails from './HeaderDetails';
import './CustomFields.css';

// ─── Types ─────────────────────────────────────────────────────────────────────

interface ConversationLayer {
  LayerId: number;
}

interface ConversationGroup {
  GroupId: number;
}

interface CustomFieldItem {
  CustomFieldId: number;
  FieldTypeId: number;
  Name: string;
  Value: string;
}

interface CustomFieldLayer {
  LayerId: number;
  IsEnable: boolean;
  IsDefault?: boolean;
  Name: string;
  Color: string;
  CustomFields: CustomFieldItem[];
}

interface Conversation {
  CoreTaskId?: number;
  PersonId?: number;
  ConversationId?: number;
  ConversationLayers: ConversationLayer[];
  ConversationGroups?: ConversationGroup[];
  ConfigValueId?: number;
  ProjectId?: string | number;
  ProjectID?: string | number;
  dashboardGroupId?: string;
  layerCustomFields?: CustomFieldLayer[];
  LayerCustomFields?: CustomFieldLayer[];
  isAddLayertoCard?: boolean;
  isFromConvertTo?: boolean;
  IsProjectConversation?: boolean;
  selectedConfigValueId?: number;
}

interface RequestConfig {
  projectId?: string | number | null;
  configValueId?: number | null;
  coreTaskId?: number | null;
  personId?: number | null;
  isDeleted: boolean;
  api: { url: string; token: string };
}

interface CustomFieldsConfig {
  api: any;
  canEdit?: boolean;
  canTemporary?: boolean;
  disableAutoSave?: boolean;
  isNew?: boolean;
  showHeader?: boolean;
  allDetails?: boolean;
  seeMore?: boolean;
  textDetailsLayers?: string;
  callApi?: boolean;
  highlight?: boolean;
  hasOwnScope?: boolean;
  ConversationIds: string | number[];
  SocketConnectionId?: string;
  onLoadDataCallback?: (
    container: HTMLDivElement | null,
    objects: ReactParamsObjects,
    functions: ReactParamsFunctions
  ) => void;
  onCustomFieldUpdated?: (field: CustomFieldItem) => void;
  onChangedProject?: (e: React.MouseEvent | undefined, params: Conversation) => void;
  onChangedSecurityGroups?: (e: React.MouseEvent | undefined, params: Conversation) => void;
  onChangedGroupLayers?: (e: React.MouseEvent | undefined, params: Conversation) => void;
  addLayerToCard?: (
    e: React.MouseEvent,
    conversation: Conversation,
    layer: ConversationLayer,
    callback: (layerData: ConversationLayer) => void
  ) => void;
}

interface ReactParamsObjects {
  data: CustomFieldLayer[];
  conversationId?: number;
  dashboardGroupIdSelected?: string;
  isProjectTitle?: boolean;
  hideImageProject?: boolean;
  hasOwnScope?: boolean;
  configValueId?: number | null;
}

interface ReactParamsFunctions {
  fncToogleShowDetails: (callApi?: boolean) => void;
  fncToogleShowAllDetails: () => void;
  fncReloadCustomFields: (reset: boolean) => void;
  fncResetLayersToHide: () => void;
  fncUpdateConversation: (conv: Conversation) => void;
  fncChangeContainer: (selector: string) => void;
  fncHide: () => void;
  fncShow: () => void;
  fncsetSeeMore: () => void;
  fncRecoverFieldsValues: (data: CustomFieldLayer[]) => void;
  fncChangedItemType: (type: { ConfigValueId: number }) => void;
  fncUpdateLayersConversation: (conv: Conversation) => void;
  fncUpdateDetails: (fields: CustomFieldLayer[]) => void;
}

export interface CustomFieldsProps {
  config: CustomFieldsConfig;
  item: Conversation;
  configValueId?: number | null;
  projectId?: number | null;
  groups?: ConversationGroup[] | null;
  section?: string;
  isProjectTitle?: boolean;
  hideImageProject?: boolean;
  containerSelector?: string;
  isProjectView?: boolean;
  isConversationDetailsView?: boolean;
  SocketConnectionId?: string;
  multiEdition?: { multiEditionCustomFields: CustomFieldLayer[] };
  onChangedLayers?: (params: Conversation, isFromInfoWindow?: boolean) => void;
  onChangedOwner?: (params: Conversation, isFromInfoWindow?: boolean) => void;
  onUpdateConversationGroups?: (conv: Conversation) => void;
  updateDetailsConversationReact?: (fields: CustomFieldLayer[], conv: Conversation) => void;
  canEditOwner?: boolean;
  getForceReloadDetailsFn?: (
    fn: (config: CustomFieldsConfig, reload: boolean) => void,
    config: CustomFieldsConfig
  ) => void;
  submitTime?: boolean;
  onOpenEditHistory?: () => void;
  updateCustomFieldsHistory?: () => void;
  editProjectDetails?: boolean;
}

// ─── LayerItem ─────────────────────────────────────────────────────────────────

interface LayerItemProps {
  disableAutoSave: boolean;
  canEdit: boolean;
  customField: CustomFieldLayer;
  configValueId?: number | null;
  projectId?: number | null;
  apiConfig: RequestConfig['api'];
  EditorEnabled: boolean;
  UpdateCustomFieldsByLayer: (layer: CustomFieldLayer) => void;
  groups?: ConversationGroup[] | null;
  isProjectTitle?: boolean;
  hideImageProject?: boolean;
  onCustomFieldUpdated?: ((field: CustomFieldItem) => void) | null;
  isConversationDetailsView?: boolean;
  SocketConnectionId?: string;
  conversation: Conversation;
  conversationIds: string;
}

const LayerItem: React.FC<LayerItemProps> = ({
  disableAutoSave,
  canEdit,
  customField,
  configValueId,
  projectId,
  apiConfig,
  EditorEnabled,
  UpdateCustomFieldsByLayer,
  groups,
  isProjectTitle,
  hideImageProject,
  onCustomFieldUpdated,
  isConversationDetailsView,
  SocketConnectionId,
  conversation,
  conversationIds,
}) =>
{
  const copyCustomField = { ...customField };

  if (isProjectTitle)
  {
    copyCustomField.CustomFields = customField.CustomFields.filter(
      f => f.FieldTypeId === 6 || f.FieldTypeId === 7,
    );
  }

  if (hideImageProject && customField.IsDefault)
  {
    copyCustomField.CustomFields = customField.CustomFields.filter(f => f.Name !== 'Photo');
  }

  if (copyCustomField.CustomFields.length === 0) return null;

  return (
    <div className="color-wrapper">
      <div
        className="top-border enable-help-tooltip layer-details-tooltip"
        data-title={customField.Name}
        style={{ borderColor: customField.Color }}
      />
      <LayersDetails
        disableAutoSave={disableAutoSave}
        conversation={conversation as any}
        canEdit={canEdit}
        customField={copyCustomField as any}
        apiConfig={apiConfig as any}
        EditorEnabled={EditorEnabled}
        configValueId={configValueId as any}
        projectId={projectId as any}
        UpdateCustomFieldsByLayer={UpdateCustomFieldsByLayer as any}
        groups={groups as any}
        onCustomFieldUpdated={onCustomFieldUpdated as any}
        isConversationDetailsView={isConversationDetailsView}
        SocketConnectionId={SocketConnectionId}
        conversationIds={conversationIds}
      />
    </div>
  );
};

// ─── Helpers ───────────────────────────────────────────────────────────────────

function isValidLayer(layer: CustomFieldLayer, layersToHide: number[]): boolean
{
  if (!layer.IsEnable || !layer.CustomFields?.length) return false;
  if (!layersToHide?.length) return true;
  return !layersToHide.includes(layer.LayerId);
}

function splitLayersBySelection(
  customFields: CustomFieldLayer[],
  selectedLayers: ConversationLayer[],
): { sLayers: CustomFieldLayer[]; uLayers: CustomFieldLayer[] }
{
  const sLayers = selectedLayers
    .map(sel => customFields.find(cf => cf.LayerId === sel.LayerId))
    .filter((cf): cf is CustomFieldLayer => cf !== undefined);

  const uLayers = customFields.filter(
    cf => !selectedLayers.some(sel => sel.LayerId === cf.LayerId),
  );

  return { sLayers, uLayers };
}

// ─── Main Component ────────────────────────────────────────────────────────────

const CustomFields: React.FC<CustomFieldsProps> = props =>
{
  const {
    config,
    item,
    configValueId: configValueIdProp,
    projectId: projectIdProp,
    groups: groupsProp,
    isProjectTitle,
    hideImageProject,
    isProjectView,
    isConversationDetailsView: _isConversationDetailsView,
    multiEdition,
    onChangedLayers: _onChangedLayers,
    onChangedOwner: _onChangedOwner,
    onUpdateConversationGroups: _onUpdateConversationGroups,
    updateDetailsConversationReact,
    canEditOwner: _canEditOwner,
    getForceReloadDetailsFn,
  } = props;

  const apiConfig = config.api;

  const [customFieldsbyLayer, setCustomFieldsbyLayer] = useState<CustomFieldLayer[]>([]);
  const [requestConfig, setRequestConfig] = useState<RequestConfig>({
    projectId: projectIdProp,
    configValueId: configValueIdProp,
    coreTaskId: item?.CoreTaskId ?? null,
    personId: item?.PersonId ?? null,
    isDeleted: false,
    api: { url: apiConfig.url, token: apiConfig.token },
  });
  const [canEdit] = useState(() => !!(config.canEdit || config.canTemporary));
  const [isNew] = useState(() => !!config.isNew);
  const [showHeader, _setShowHeader] = useState(config.showHeader ?? true);
  const [showDetails, setShowDetails] = useState(true);
  const [allDetails, setAllDetails] = useState(config.allDetails ?? false);
  const [seeMore, setSeeMoreState] = useState(config.seeMore ?? false);
  const [_textDetailsLayers, setTextDetailsLayers] = useState(
    config.textDetailsLayers ?? 'See more details',
  );
  const [EditorEnabled] = useState(false);
  const [layersToHide, setLayersToHide] = useState<number[]>([]);
  const [groups, setGroups] = useState(groupsProp ?? null);
  const [highlight, setHighlight] = useState(false);
  const [_sLayers, setSLayers] = useState<CustomFieldLayer[]>([]);
  const [_uLayers, setULayers] = useState<CustomFieldLayer[]>([]);
  const [prevCustomFieldId, setPrevCustomFieldId] = useState(configValueIdProp);
  const [conversationUpdated, setConversationUpdated] = useState<Conversation | undefined>();
  const [_containerSelector, setContainerSelector] = useState<string | undefined>();

  // const _isRecycleBin = window.location.href.includes("RecycleBin");
  // const _isProjectViewState = window.location.href.includes("ProjectDetails");

  const containerRef = useRef<HTMLDivElement>(null);
  const headerDetailsRef = useRef<React.ComponentRef<typeof HeaderDetails>>(null);

  // ── Helpers ──────────────────────────────────────────────────────────────

  const highlightCustomFields = useCallback(() =>
  {
    setHighlight(true);
    setTimeout(() => setHighlight(false), 2000);
  }, []);

  const computeLayerSplit = useCallback(
    (fields: CustomFieldLayer[], layers: ConversationLayer[]) =>
    {
      const { sLayers: s, uLayers: u } = splitLayersBySelection(fields, layers);
      setSLayers(s);
      setULayers(u);
    },
    [],
  );

  // ── sendDataToCallback ────────────────────────────────────────────────────

  const sendDataToCallback = useCallback(
    (data: CustomFieldLayer[]) =>
    {
      if (!config.onLoadDataCallback) return;

      const objects: ReactParamsObjects = {
        data,
        conversationId: item?.ConversationId,
        dashboardGroupIdSelected: item?.dashboardGroupId,
        isProjectTitle,
        hideImageProject,
        hasOwnScope: config.hasOwnScope,
        configValueId: configValueIdProp,
      };

      const functions: ReactParamsFunctions = {
        fncToogleShowDetails: toogleShowDetails,
        fncToogleShowAllDetails: toogleShowAllDetails,
        fncReloadCustomFields: reloadCustomField,
        fncResetLayersToHide: resetLayersToHide,
        fncUpdateConversation: onUpdateConversation,
        fncChangeContainer: onChangeContainer,
        fncHide: hideDetails,
        fncShow: showDetailsMethod,
        fncsetSeeMore: setSeeMore,
        fncRecoverFieldsValues: onRecoverFieldsValues,
        fncChangedItemType: onChangedItemType,
        fncUpdateLayersConversation: onUpdateLayersConversation,
        fncUpdateDetails: onUpdateDetails,
      };

      config.onLoadDataCallback(containerRef.current, objects, functions);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [config, item, isProjectTitle, hideImageProject, configValueIdProp],
  );

  // ── Data fetching ─────────────────────────────────────────────────────────

  const retrieveCustomDetailsData = useCallback(
    (response: CustomFieldLayer[], hl?: boolean) =>
    {
      if (hl) highlightCustomFields();
      sendDataToCallback(response);
      setCustomFieldsbyLayer(response);
      if (item?.ConversationLayers)
      {
        computeLayerSplit(response, item.ConversationLayers);
      }
    },
    [highlightCustomFields, sendDataToCallback, item, computeLayerSplit],
  );

  const retrieveMultipleCustomDetailsData = useCallback((fields: CustomFieldLayer[]) =>
  {
    setCustomFieldsbyLayer(fields);
  }, []);

  const getCustomFieldsData = useCallback(
    (cfg: Partial<CustomFieldsConfig>, _isReload: boolean) =>
    {
      const callApi = cfg.callApi === undefined || cfg.callApi === null || cfg.callApi !== false;
      const itemConv = conversationUpdated ?? item;

      if (!callApi || !itemConv) return;

      const highlight = cfg.highlight;

      if (isNew)
      {
        CustomFieldsService.getEmptyCustomFieldsByConfigValueId(requestConfig as any)
          .then(res => retrieveCustomDetailsData(res as CustomFieldLayer[], highlight))
          .catch(err => console.warn('Api Error', err));
        return;
      }

      if (itemConv.PersonId && itemConv.PersonId > 0 && (itemConv.ConversationId ?? 0) > 0)
      {
        (CustomFieldsService as any)
          .getPersonCustomFieldsByItemId(requestConfig)
          .then((res: any) => retrieveCustomDetailsData(res as CustomFieldLayer[], highlight))
          .catch((err: any) => console.warn('Api Error', err));
        return;
      }

      const coreTaskId = itemConv.CoreTaskId ?? 0;
      const conversationId = itemConv.ConversationId ?? 0;

      if (coreTaskId > 0 || itemConv.IsProjectConversation || conversationId > 0)
      {
        if (itemConv.IsProjectConversation || isProjectTitle)
        {
          CustomFieldsService.getProjectCustomFieldsByItemId(requestConfig as any)
            .then(res => retrieveCustomDetailsData(res as CustomFieldLayer[], highlight))
            .catch(err => console.warn('Api Error', err));
        }
        else if (coreTaskId > 0)
        {
          CustomFieldsService.getCustomFieldsByItemId(requestConfig as any)
            .then(res => retrieveCustomDetailsData(res as CustomFieldLayer[], highlight))
            .catch(err => console.warn('Api Error', err));
        }
        else if (conversationId > 0)
        {
          CustomFieldsService.getLayersByConfigValueId(requestConfig as any)
            .then(res => retrieveCustomDetailsData(res as CustomFieldLayer[], highlight))
            .catch(err => console.warn('Api Error', err));
        }
      }
    },
    [conversationUpdated, item, isNew, isProjectTitle, requestConfig, retrieveCustomDetailsData],
  );

  const getCustomFieldsMultipleData = useCallback(
    (_cfg: CustomFieldsConfig, _isReload: boolean) =>
    {
      if (multiEdition?.multiEditionCustomFields)
      {
        retrieveMultipleCustomDetailsData(multiEdition.multiEditionCustomFields);
      }
    },
    [multiEdition, retrieveMultipleCustomDetailsData],
  );

  // ── Lifecycle ─────────────────────────────────────────────────────────────

  // componentWillMount equivalent — runs once before first render
  useEffect(() =>
  {
    if (!CorgeeStorageHelper.isAuthenticated(config))
    {
      console.error('CustomFields React Component: You have not authenticated.');
      return;
    }

    setTimeout(() =>
    {
      getCustomFieldsMultipleData(config, true);
      if (typeof getForceReloadDetailsFn === 'function')
      {
        getForceReloadDetailsFn(getCustomFieldsMultipleData, config);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // componentDidMount equivalent
  useEffect(() =>
  {
    const layerFields = item?.layerCustomFields ?? item?.LayerCustomFields;
    if (item && config.callApi === false && layerFields)
    {
      const data = Object.values(layerFields).filter(Boolean) as CustomFieldLayer[];
      setCustomFieldsbyLayer(data);
    }
    setLayersToHide(CorgeeStorageHelper.getLayersToHide() as any);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // componentWillReceiveProps equivalent
  useEffect(() =>
  {
    setLayersToHide(CorgeeStorageHelper.getLayersToHide() as any);

    if ((!groups || groups.length === 0) && groupsProp && groupsProp.length > 0)
    {
      setGroups(groupsProp);
    }
  }, [groupsProp, groups]);

  useEffect(() =>
  {
    if (item?.isAddLayertoCard) onUpdateLayersConversation(item);
    if (item?.isFromConvertTo)
    {
      onUpdateConversation(item);
      onUpdateLayersConversation(item);
      reloadCustomField(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item]);

  useEffect(() =>
  {
    if (projectIdProp !== undefined && showDetails === true)
    {
      setRequestConfig(prev => ({ ...prev, projectId: projectIdProp }));
      //   setTimeout(() => getCustomFieldsData({ callApi: true }, true));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectIdProp]);

  // ── Methods ───────────────────────────────────────────────────────────────

  const resetLayersToHide = useCallback(() =>
  {
    setLayersToHide(CorgeeStorageHelper.getLayersToHide() as any);
  }, []);

  const onRecoverFieldsValues = useCallback((data: CustomFieldLayer[]) =>
  {
    if (data?.length > 0) setCustomFieldsbyLayer(data);
  }, []);

  const onChangeContainer = useCallback((selector: string) =>
  {
    setContainerSelector(selector);
  }, []);

  const onUpdateConversation = useCallback((conversation: Conversation) =>
  {
    setRequestConfig(prev => ({
      ...prev,
      projectId: conversation.ProjectId,
      coreTaskId: conversation.CoreTaskId,
    }));
    setConversationUpdated(conversation);
    setGroups(conversation.ConversationGroups ?? null);
    (headerDetailsRef.current as any)?.updateConversation(conversation);
    onReloadLayers(conversation.ConversationLayers);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onUpdateLayersConversation = useCallback((conversation: Conversation) =>
  {
    setConversationUpdated(conversation);
    (headerDetailsRef.current as any)?.onUpdateLayersConversation(conversation.ConversationLayers);
  }, []);

  const onReloadLayers = useCallback(
    (layers: ConversationLayer[]) =>
    {
      computeLayerSplit(customFieldsbyLayer, layers);
    },
    [customFieldsbyLayer, computeLayerSplit],
  );

  const onUpdateDetails = useCallback((layerCustomFields: CustomFieldLayer[]) =>
  {
    setCustomFieldsbyLayer(layerCustomFields);
    setConversationUpdated(prev => (prev ? { ...prev, layerCustomFields } : prev));
  }, []);

  const onChangedItemType = useCallback(
    (newItemType: { ConfigValueId: number }) =>
    {
      const newConfig = { ...requestConfig, configValueId: newItemType.ConfigValueId };
      setRequestConfig(newConfig);

      if (isNew || prevCustomFieldId !== newConfig.configValueId)
      {
        CustomFieldsService.getEmptyCustomFieldsByConfigValueId(newConfig as any)
          .then(response =>
          {
            retrieveCustomDetailsData(response as CustomFieldLayer[]);
            setCustomFieldsbyLayer(response as CustomFieldLayer[]);
            setPrevCustomFieldId(newConfig.configValueId);
            (headerDetailsRef.current as any)?.reloadLayers?.();
          })
          .catch(err => console.warn('Api Error', err));

        (headerDetailsRef.current as any)?.onChangedItemType?.(newItemType);
      }
    },
    [requestConfig, isNew, prevCustomFieldId, retrieveCustomDetailsData],
  );

  const UpdateCustomFieldsByLayer = (layerCustomFieldsUpdated: CustomFieldLayer) =>
  {
    setCustomFieldsbyLayer(prev =>
    {
      const idx = prev.findIndex(l => l.LayerId === layerCustomFieldsUpdated.LayerId);
      if (idx < 0) return prev;
      const next = [...prev];
      next[idx] = layerCustomFieldsUpdated;
      return next;
    });
  };

  useEffect(() =>
  {
    if (updateDetailsConversationReact && item)
    {
      const conv = { ...item };
      if (conv.ConfigValueId === 9) conv.ConversationGroups = groups ?? undefined;
      updateDetailsConversationReact(customFieldsbyLayer, conv);
    }
  }, [customFieldsbyLayer]);

  const reloadCustomField = useCallback(
    (resetRequestState: boolean) =>
    {
      setTimeout(() =>
      {
        if (resetRequestState)
        {
          setRequestConfig(prev => ({
            ...prev,
            projectId: projectIdProp,
            configValueId: configValueIdProp,
            coreTaskId: item?.CoreTaskId ?? null,
            personId: item?.PersonId ?? null,
          }));
        }
        setTimeout(() => getCustomFieldsData(config, true));
      });
    },
    [projectIdProp, configValueIdProp, item, config, getCustomFieldsData],
  );

  // ── View toggles ──────────────────────────────────────────────────────────

  const toogleShowDetails = useCallback(
    (callApi = true) =>
    {
      let nextShow = !showDetails;

      if (isProjectView)
      {
        nextShow = true;
        setContainerSelector('#canvasDetails');
      }
      else
      {
        const selector = `li[data-conversationid=${item?.ConversationId}] .show-details-panel`;
        setContainerSelector(selector);
        if (callApi) setShowDetails(nextShow);
      }

      if (nextShow)
      {
        setTimeout(() => getCustomFieldsData({ callApi }, true));
      }

      setTextDetailsLayers('See more details');
    },
    [showDetails, isProjectView, item, getCustomFieldsData],
  );

  const toogleShowAllDetails = useCallback(() =>
  {
    setAllDetails(true);
    setTextDetailsLayers('See less details');
  }, []);

  const hideDetails = useCallback(() =>
  {
    setShowDetails(false);
    setAllDetails(false);
    setSeeMoreState(false);
  }, []);

  const showDetailsMethod = useCallback(() =>
  {
    setContainerSelector(`li[data-conversationid='${item?.ConversationId}'] .show-details-panel`);
    setShowDetails(true);
  }, [item]);

  const setSeeMore = useCallback(() =>
  {
    if (seeMore || allDetails)
    {
      setAllDetails(false);
      setTextDetailsLayers('See more details');
    }
    else
    {
      setAllDetails(true);
      setTextDetailsLayers('See less details');
    }
    setSeeMoreState(prev => prev);
  }, [seeMore, allDetails]);

  // ── Render ─────────────────────────────────────────────────────────────────

  if (!item) return null;

  const onCustomFieldUpdated = config.onCustomFieldUpdated ?? null;
  const showHeaderValidated = isProjectTitle ? false : showHeader;
  const conversationIds = config.ConversationIds.toString();

  return (
    <div
      data-is-react="true"
      ref={containerRef}
      className={
        (showDetails
          ? 'details-tab-animation-content layer-details-container'
          : 'show-details-panel') + (highlight ? ' react-item-highlighted' : '')
      }
    >
      {showDetails && showHeaderValidated && (
        <HeaderDetails
          {...(props as any)}
          config={{
            url: config.api?.url,
            authToken: config.api?.token,
            addLayerToCard: config.addLayerToCard,
            onChangedProject: config.onChangedProject,
            onChangedSecurityGroups: config.onChangedSecurityGroups,
            onChangedGroupLayers: config.onChangedGroupLayers,
          }}
          isNew={isNew}
          disableAutoSave
          ref={headerDetailsRef}
          sendDataToCallback={sendDataToCallback as any}
          onUpdateConversationGroups={onUpdateConversation as any}
          onReloadLayers={onReloadLayers}
        />
      )}

      {showDetails &&
        customFieldsbyLayer.map(customField =>
          customField?.IsEnable && isValidLayer(customField, layersToHide) ? (
            <LayerItem
              key={`l-cf-${customField.LayerId}`}
              conversation={item}
              disableAutoSave
              canEdit={canEdit}
              customField={customField}
              configValueId={requestConfig.configValueId}
              projectId={projectIdProp}
              apiConfig={requestConfig.api}
              EditorEnabled={EditorEnabled}
              UpdateCustomFieldsByLayer={UpdateCustomFieldsByLayer}
              groups={groups}
              isProjectTitle={isProjectTitle}
              hideImageProject={hideImageProject}
              onCustomFieldUpdated={onCustomFieldUpdated}
              SocketConnectionId={config.SocketConnectionId}
              conversationIds={conversationIds}
            />
          ) : null,
        )}
    </div>
  );
};

(CustomFields as any).defaultProps = {
  config: {} as CustomFieldsConfig,
  configValueId: null,
  projectId: null,
  groups: null,
  section: '',
  isProjectView: false,
};

export default CustomFields;
